import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hourglass, Receipt, RefreshCw } from 'lucide-react';
import type { PedidoEnBandejaVista } from '@shared/types';
import { COLUMNAS_BANDEJA, TITULO_COLUMNA, type ColumnaBandeja } from '@shared/bandejaPedidos';
import type { AccionBandeja } from '@shared/accionBandeja';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { TarjetaBandeja } from '@renderer/components/bandeja/TarjetaBandeja';
import { CobroModal } from '@renderer/components/cobro/CobroModal';
import { TicketPreviewModal } from '@renderer/components/ticket/TicketPreviewModal';

/**
 * Sprint 123 — Los pedidos, lleguen por donde lleguen.
 *
 * ## Qué reemplaza
 *
 * A los tableros de **Mostrador** y **A domicilio**, que hoy son entradas separadas del menú junto
 * a `Pedidos`. Tres sitios donde mirar para responder una sola pregunta: *¿qué me falta?*.
 *
 * Del dueño, describiendo el día de quien atiende sola: *"todo el día son atender los pedidos **por
 * donde lleguen**"*. El sistema la obligaba a lo contrario — elegir el canal antes de poder
 * atender.
 *
 * **`Mesas` no se toca**: no es una lista de pedidos, es el plano del salón. Responde *"¿qué mesa
 * está libre?"*, que es otra pregunta.
 *
 * ## Por qué la franja de arriba está FUERA de las columnas
 *
 * Las tres esperas de domicilio —que el cliente mande su ubicación, que conteste el grupo de
 * motomandados, que llegue la captura de la transferencia— **no dependen del negocio**. El Sprint
 * 76 las modeló precisamente porque tratarlas como trabajo en curso obligaba a terminar un cliente
 * para poder empezar otro.
 *
 * Meterlas en "Falta cobrar" —que es donde caerían por descarte— juntaría *"no cocines hasta que
 * transfiera"*, con su reloj de 15 minutos corriendo, con *"la moto volvió con el efectivo"*. Son
 * extremos opuestos del flujo.
 *
 * ## Lo que esta pantalla NO decide
 *
 * Ni la columna ni la acción: las dos salen de módulos puros compartidos con el servidor
 * (`shared/bandejaPedidos.ts` y `shared/accionBandeja.ts`), probados con la matriz completa de
 * combinaciones. Aquí sólo se dibuja y se conectan los modales que ya existen.
 */
export function BandejaPedidosPage() {
  const navigate = useNavigate();
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [pedidos, setPedidos] = useState<PedidoEnBandejaVista[]>([]);
  const [entregadosHoy, setEntregadosHoy] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [cobroId, setCobroId] = useState<number | null>(null);
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [previewPdfPath, setPreviewPdfPath] = useState<string | null>(null);
  const [desactualizada, setDesactualizada] = useState(false);

  /**
   * Sprint 126 — Antes esto era `if (r.ok) setPedidos(r.data)` y nada más.
   *
   * Se descubrió recorriendo la pantalla contra el sitio real: se cobró el #00001, el ticket salió
   * bien, y la tarjeta se quedó atrás mostrando *"Cobrar"* — cuando el servidor, preguntado por
   * separado, ya respondía *"Se lo llevó"*. La pantalla enseñaba un pasado y **nadie se enteraba**,
   * porque una carga fallida no dejaba rastro: ni aviso, ni log, ni cambio visible.
   *
   * Es el peor modo de fallo para esta pantalla en concreto: no muestra un error, muestra un
   * **estado plausible pero viejo**, y sobre eso se toman decisiones de dinero. Vale más una tarjeta
   * que confiesa estar desactualizada que una que miente con seguridad.
   */
  const cargar = useCallback(async () => {
    const [r, ent] = await Promise.all([
      window.api.pedidos.bandeja(),
      window.api.pedidos.domiciliosEntregadosHoy(),
    ]);
    if (r.ok) setPedidos(r.data);
    if (ent.ok) setEntregadosHoy(ent.data);
    setDesactualizada(!r.ok);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
    const t = setInterval(() => void cargar(), 15000);
    return () => clearInterval(t);
  }, [cargar]);

  /**
   * Sprint 126 — Avisar **y volver a cargar**.
   *
   * Antes sólo avisaba y se quedaba con la tarjeta vieja en pantalla, así que el siguiente toque
   * daba el mismo error: un bucle donde el sistema repite "no puedo" sin arreglar lo que lo causa.
   *
   * Casi todos estos fallos son la misma historia —*"este pedido ya tiene un pago registrado"*—, y
   * significan que la pantalla se quedó atrás. Refrescar convierte el error en una corrección: la
   * tarjeta se acomoda sola y el botón pasa a ser el que de verdad toca.
   */
  const fallo = async (titulo: string, e: { error?: string; codigo?: string }) => {
    await window.alertar({ titulo, mensaje: traducirErrorBackend(e.error, e.codigo), variant: 'warn' });
    await cargar();
  };

  /**
   * Cada acción llama a la operación que **ya existía** en su tablero de origen. La bandeja no
   * inventa operaciones: las reúne. Por eso todas están probadas desde antes.
   *
   * Las que abren un modal propio del domicilio —cotizar el envío, la espera del comprobante,
   * asignar la moto— navegan por ahora al tablero correspondiente, y ahí se resuelven. Se dice
   * en voz alta: **hasta que esos modales vivan aquí, `A domicilio` no se retira del menú.**
   */
  const ejecutar = async (accion: AccionBandeja, p: PedidoEnBandejaVista) => {
    switch (accion) {
      case 'empaquetar': {
        const r = await window.api.pedidos.marcarEmpaquetado(p.id);
        if (!r.ok) return fallo('No se pudo marcar como listo', r);
        break;
      }
      case 'marcar_listo': {
        const r = await window.api.pedidos.marcarListo(p.id);
        if (!r.ok) return fallo('No se pudo marcar como listo', r);
        break;
      }
      case 'entregar_mostrador': {
        const r = await window.api.pedidos.marcarEntregadoMostrador({ pedidoId: p.id });
        if (!r.ok) return fallo('No se pudo cerrar el pedido', r);
        break;
      }
      case 'entregar_mesa': {
        const r = await window.api.pedidos.marcarEntregadoMesa({ pedidoId: p.id });
        if (!r.ok) return fallo('No se pudo marcar la entrega', r);
        break;
      }
      case 'entregar_domicilio': {
        const r = await window.api.pedidos.marcarEntregado({ pedidoId: p.id });
        if (!r.ok) return fallo('No se pudo marcar la entrega', r);
        break;
      }
      case 'dar_por_cerrado': {
        const r = await window.api.pedidos.cerrarEntregadoPagado({ pedidoId: p.id });
        if (!r.ok) return fallo('No se pudo cerrar', r);
        break;
      }
      case 'cobrar':
        setCobroId(p.id);
        return;
      // Las esperas del domicilio se atienden en su tablero, que aún conserva sus modales.
      case 'cotizar_envio':
      case 'revisar_del_enlace':
      case 'esperando_pago':
      case 'asignar_moto':
        navigate('/domicilios');
        return;
    }
    await cargar();
  };

  const enEspera = pedidos.filter((p) => p.columna === null);
  const porColumna = (c: ColumnaBandeja) => pedidos.filter((p) => p.columna === c);
  const total = pedidos.reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Receipt className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Pedidos</h1>
            <p className="text-sm text-slate-500">
              {pedidos.length === 0
                ? 'Nada pendiente por ahora.'
                : `${pedidos.length} en curso · ${formatPrecio(total, monedaSimbolo, locale)}`}
              {entregadosHoy > 0 && (
                <span className="ml-1 text-emerald-700">
                  {pedidos.length > 0 ? ' · ' : ''}
                  <strong>{entregadosHoy}</strong> {entregadosHoy === 1 ? 'entregado' : 'entregados'} hoy
                </span>
              )}
            </p>
          </div>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost" aria-label="Refrescar">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/*
        Sprint 126 — Cuando la pantalla no pudo actualizarse, lo dice. Sale arriba de todo y en
        ámbar porque lo que hay debajo puede estar viejo, y aquí abajo se cobra.
      */}
      {desactualizada && (
        <div className="flex min-h-[44px] items-center gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800">
          <RefreshCw className="h-4 w-4 shrink-0" />
          <span>No pude actualizar la lista. Lo que ves puede estar atrasado.</span>
          <button onClick={() => void cargar()} className="btn-secondary ml-auto min-h-[44px] shrink-0">
            Reintentar
          </button>
        </div>
      )}

      {/*
        La franja de esperas. Sólo aparece cuando hay algo: una franja vacía permanente es ruido, y
        el objetivo de esta pantalla es que lo que se ve sea lo que hay que atender.
      */}
      {enEspera.length > 0 && (
        <section className="rounded-xl border-2 border-violet-200 bg-violet-50 p-3">
          <h2 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-violet-700">
            <Hourglass className="h-3.5 w-3.5" />
            Esperando al cliente — no depende de ti
          </h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {enEspera.map((p) => (
              <TarjetaBandeja key={p.id} pedido={p} onAccion={ejecutar} />
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {COLUMNAS_BANDEJA.map((clave) => {
          const lista = porColumna(clave);
          return (
            <section key={clave} className="card overflow-hidden p-0">
              <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
                <h2 className="text-sm font-semibold text-slate-700">{TITULO_COLUMNA[clave]}</h2>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600">
                  {lista.length}
                </span>
              </header>
              <div className="space-y-2.5 p-3">
                {cargando ? (
                  <p className="py-6 text-center text-sm text-slate-400">Cargando…</p>
                ) : lista.length === 0 ? (
                  <p className="py-6 text-center text-sm text-slate-400">Nada por aquí.</p>
                ) : (
                  lista.map((p) => <TarjetaBandeja key={p.id} pedido={p} onAccion={ejecutar} />)
                )}
              </div>
            </section>
          );
        })}
      </div>

      <CobroModal
        abierto={cobroId !== null}
        pedidoId={cobroId}
        onClose={() => {
          setCobroId(null);
          void cargar();
        }}
        onCobrado={(pdfPath) => {
          // Sprint 112 — cobrar y no emitir el ticket dejó al cliente sin comprobante. No se repite.
          setPreviewPdfPath(pdfPath ?? null);
          setPreviewId(cobroId);
          setCobroId(null);
          void cargar();
        }}
      />

      <TicketPreviewModal
        abierto={previewId !== null}
        pedidoId={previewId}
        pdfPath={previewPdfPath}
        onClose={() => {
          setPreviewId(null);
          setPreviewPdfPath(null);
          void cargar();
        }}
      />
    </div>
  );
}
